<template>
    <section class="main-content is-transparent">
        <div class="hero">
            <div class="hero-body">
                <h1
                    class="title is-2 is-uppercase has-text-primary has-text-weight-bold"
                >
                    {{ $t("privacy.title") }}
                </h1>
                <p><b>Ultimo aggiornamento 01 luglio 2023</b></p>
                <p>
                    La presente informativa sulla privacy per Nuovo Rinascimento
                    APS ("<b>noi</b>", "<b>ci</b>" o "<b>nostro</b>"), descrive
                    come e perché potremmo raccogliere, archiviare, utilizzare
                    e/o condividere ("elaborare") le tue informazioni quando
                    utilizzi i nostri servizi ("<b>Servizi</b>"), ad esempio
                    quando:
                </p>
                <ul>
                    <li>
                        Visita il nostro sito Web
                        <a href="https://www.tempiodelfuturo.art"
                            >www.tempiodelfuturo.art</a
                        >
                        o qualsiasi nostro sito Web collegato alla presente
                        informativa sulla privacy
                    </li>
                </ul>
                <ul>
                    <li>
                        Scarica e utilizza la nostra applicazione mobile
                        (TDF*P), o qualsiasi altra nostra applicazione collegata
                        alla presente informativa sulla privacy
                    </li>
                </ul>
                <ul>
                    <li>
                        Interagire con noi in altri modi correlati, tra cui
                        vendite, marketing o eventi
                    </li>
                </ul>
                <p>
                    <strong>Domande o dubbi?</strong> La lettura di questa
                    informativa sulla privacy ti aiuterà a comprendere i tuoi
                    diritti e le tue scelte in materia di privacy. Se non sei
                    d'accordo con le nostre politiche e pratiche, ti preghiamo
                    di non utilizzare i nostri Servizi. Se hai ancora domande o
                    dubbi, ti preghiamo di contattarci all'indirizzo
                    amm.nuovorinascimentoitaliano@gmail.com.
                </p>
                <p><strong>SINTESI DEI PUNTI CHIAVE</strong></p>
                <p>
                    <strong
                        >Questo riepilogo fornisce i punti chiave della nostra
                        informativa sulla privacy, ma puoi trovare maggiori
                        dettagli su uno qualsiasi di questi argomenti facendo
                        clic sul link che segue ciascun punto chiave o
                        utilizzando il nostro
                        <a href="#toc"><b>sommario</b></a> qui sotto per trovare
                        la sezione che stai cercando.</strong
                    >
                </p>
                <p>
                    <strong>Quali informazioni personali trattiamo?</strong>
                    Quando visiti, utilizzi o navighi nei nostri Servizi,
                    potremmo elaborare informazioni personali a seconda di come
                    interagisci con noi e i Servizi, delle scelte che fai e dei
                    prodotti e delle funzionalità che utilizzi. Ulteriori
                    informazioni sulle
                    <a href="#personalinfo"
                        >informazioni personali che ci comunichi</a
                    >.
                </p>
                <p>
                    <strong
                        >Elaboriamo informazioni personali sensibili?</strong
                    >
                    Non trattiamo informazioni personali sensibili.
                </p>
                <p>
                    <strong>Riceviamo informazioni da terze parti?</strong> Non
                    riceviamo alcuna informazione da terzi.
                </p>
                <p>
                    <strong>Come trattiamo le tue informazioni?</strong>
                    Elaboriamo le tue informazioni per fornire, migliorare e
                    amministrare i nostri Servizi, comunicare con te, per la
                    sicurezza e la prevenzione delle frodi e per rispettare la
                    legge. Potremmo anche elaborare le tue informazioni per
                    altri scopi con il tuo consenso. Elaboriamo le tue
                    informazioni solo quando abbiamo un valido motivo legale per
                    farlo. Scopri di più su
                    <a href="#infouse">come trattiamo le tue informazioni</a>.
                </p>
                <p>
                    <strong
                        >In quali situazioni e con quali tipi di parti
                        condividiamo le informazioni personali?</strong
                    >
                    Possiamo condividere informazioni in situazioni specifiche e
                    con categorie specifiche di terze parti. Scopri di più su
                    <a href="#whoshare"
                        >quando e con chi condividiamo le tue informazioni
                        personali</a
                    >.
                </p>
                <p>
                    <strong
                        >Come manteniamo le tue informazioni al sicuro?</strong
                    >
                    Disponiamo di processi e procedure organizzative e tecniche
                    per proteggere le tue informazioni personali. Tuttavia,
                    nessuna trasmissione elettronica su Internet o tecnologia di
                    archiviazione delle informazioni può essere garantita come
                    sicura al 100%, quindi non possiamo promettere o garantire
                    che hacker, criminali informatici o altre terze parti non
                    autorizzate non saranno in grado di sconfiggere la nostra
                    sicurezza e raccogliere, accedere, rubare o modificare
                    impropriamente le tue informazioni. Scopri di più su
                    <a href="#infosafe">come proteggiamo le tue informazioni</a
                    >.
                </p>
                <p>
                    <strong>Quali sono i tuoi diritti?</strong> A seconda di
                    dove ti trovi geograficamente, la legge sulla privacy
                    applicabile potrebbe significare che hai determinati diritti
                    relativi alle tue informazioni personali. Ulteriori
                    informazioni sui
                    <a href="#privacyrights">tuoi diritti alla privacy</a>.
                </p>
                <p>
                    <strong>Come esercitate i vostri diritti?</strong> Il modo
                    più semplice per esercitare i tuoi diritti è inviare una
                    <a
                        href="https://app.termly.io/notify/ae923c0a-fbc6-4be3-8855-737fa7a65665"
                        >richiesta</a
                    >
                    di accesso ai dati dell'interessato o contattarci.
                    Prenderemo in considerazione e agiremo su qualsiasi
                    richiesta in conformità con le leggi sulla protezione dei
                    dati applicabili.
                </p>
                <p>
                    Vuoi saperne di più su cosa facciamo con le informazioni che
                    raccogliamo?
                    <a href="#toc"
                        >Consulta l'informativa sulla privacy per intero</a
                    >.
                </p>
                <p><strong>SOMMARIO</strong></p>
                <p>
                    <a href="#infocollect"
                        >1. QUALI INFORMAZIONI RACCOGLIAMO?</a
                    >
                </p>
                <p>
                    <a href="#infouse"
                        >2. COME TRATTIAMO LE TUE INFORMAZIONI?</a
                    >
                </p>
                <p>
                    <a href="#legalbases"
                        >3. SU QUALI BASI GIURIDICHE CI BASIAMO PER ELABORARE LE
                        TUE INFORMAZIONI PERSONALI?</a
                    >
                </p>
                <p>
                    <a href="#whoshare"
                        >4. QUANDO E CON CHI CONDIVIDIAMO LE TUE INFORMAZIONI
                        PERSONALI?</a
                    >
                </p>
                <p>
                    <a href="#cookies"
                        >5. UTILIZZIAMO COOKIE E ALTRE TECNOLOGIE DI
                        TRACCIAMENTO?</a
                    >
                </p>
                <p>
                    <a href="#inforetain"
                        >6. PER QUANTO TEMPO CONSERVIAMO LE TUE INFORMAZIONI?</a
                    >
                </p>
                <p>
                    <a href="#infosafe"
                        >7. COME MANTENIAMO LE TUE INFORMAZIONI AL SICURO?</a
                    >
                </p>
                <p>
                    <a href="#privacyrights"
                        >8. QUALI SONO I TUOI DIRITTI SULLA PRIVACY?</a
                    >
                </p>
                <p>
                    <a href="#DNT">9. CONTROLLI PER LE FUNZIONI DO-NOT-TRACK</a>
                </p>
                <p>
                    <a href="#caresidents"
                        >10. I RESIDENTI IN CALIFORNIA HANNO SPECIFICI DIRITTI
                        ALLA PRIVACY?</a
                    >
                </p>
                <p>
                    <a href="#virginia"
                        >11. I RESIDENTI IN VIRGINIA HANNO SPECIFICI DIRITTI
                        SULLA PRIVACY?</a
                    >
                </p>
                <p>
                    <a href="#policyupdates"
                        >12. APPORTIAMO AGGIORNAMENTI ALLA PRESENTE
                        INFORMATIVA?</a
                    >
                </p>
                <p>
                    <a href="#contact"
                        >13. COME PUOI CONTATTARCI IN MERITO A QUESTA
                        INFORMATIVA?</a
                    >
                </p>
                <p>
                    <a href="#request"
                        >14. COME PUOI RIVEDERE, AGGIORNARE O ELIMINARE I DATI
                        CHE RACCOGLIAMO DA TE?</a
                    >
                </p>
                <p id="infocollect">
                    <strong>1. QUALI INFORMAZIONI RACCOGLIAMO?</strong>
                </p>
                <p><strong>Informazioni personali che ci fornisci</strong></p>
                <p>
                    <strong>In breve:</strong> raccogliamo le informazioni
                    personali che ci fornisci.
                </p>
                <p>
                    Raccogliamo le informazioni personali che ci fornisci
                    volontariamente quando ti registri sui Servizi, esprimi
                    interesse a ottenere informazioni su di noi o sui nostri
                    prodotti e Servizi, quando partecipi ad attività sui Servizi
                    o altrimenti quando ci contatti.
                </p>
                <p>
                    <strong>Informazioni personali fornite dall'utente.</strong>
                    Le informazioni personali che raccogliamo dipendono dal
                    contesto delle tue interazioni con noi e i Servizi, dalle
                    scelte che fai e dai prodotti e funzionalità che utilizzi.
                    Le informazioni personali che raccogliamo possono includere
                    quanto segue:
                </p>
                <ul>
                    <li>Nomi</li>
                </ul>
                <ul>
                    <li>indirizzi e-mail</li>
                </ul>
                <ul>
                    <li>indirizzi postali</li>
                </ul>
                <ul>
                    <li>nomi utente</li>
                </ul>
                <ul>
                    <li>Preferenze di contatto</li>
                </ul>
                <ul>
                    <li>Indirizzi di fatturazione</li>
                </ul>
                <ul>
                    <li>codice fiscale</li>
                </ul>
                <ul>
                    <li>Carta d'identità</li>
                </ul>
                <ul>
                    <li>data di nascita</li>
                </ul>
                <p>
                    <strong>Informazioni sensibili.</strong> Non trattiamo
                    informazioni sensibili.
                </p>
                <p>
                    <strong>Dati di pagamento.</strong> Potremmo raccogliere i
                    dati necessari per elaborare il pagamento se effettui
                    acquisti, come il numero dello strumento di pagamento e il
                    codice di sicurezza associato allo strumento di pagamento.
                    Tutti i dati di pagamento vengono memorizzati da PayPal.
                    Puoi trovare i loro link alla privacy
                    <a href="https://www.paypal.com/it/legalhub/privacy-full"
                        >qui</a
                    >.
                </p>
                <p>
                    <strong>Dati dell'applicazione.</strong> Se utilizzi le
                    nostre applicazioni, potremmo anche raccogliere le seguenti
                    informazioni se scegli di fornirci l'accesso o
                    l'autorizzazione:
                </p>
                <ul>
                    <li>
                        Dati del dispositivo mobile. Raccogliamo automaticamente
                        informazioni sul dispositivo (come l'ID del dispositivo
                        mobile, il modello e il produttore), il sistema
                        operativo, le informazioni sulla versione e sulla
                        configurazione del sistema, i numeri di identificazione
                        del dispositivo e dell'applicazione, il tipo e la
                        versione del browser, il provider di servizi Internet
                        del modello hardware e/o l'operatore di telefonia mobile
                        e l'indirizzo IP (Internet Protocol) (o server proxy).
                        Se stai utilizzando le nostre applicazioni, potremmo
                        anche raccogliere informazioni sulla rete telefonica
                        associata al tuo dispositivo mobile, al sistema
                        operativo o alla piattaforma del tuo dispositivo mobile,
                        al tipo di dispositivo mobile che utilizzi, all'ID
                        univoco del dispositivo mobile e alle informazioni sulle
                        funzionalità delle nostre applicazioni a cui hai avuto
                        accesso.
                    </li>
                </ul>
                <p>
                    Queste informazioni sono necessarie principalmente per
                    mantenere la sicurezza e il funzionamento delle nostre
                    applicazioni, per la risoluzione dei problemi e per i nostri
                    scopi interni di analisi e reporting.
                </p>
                <p>
                    Tutte le informazioni personali che ci fornisci devono
                    essere veritiere, complete e accurate e devi informarci di
                    eventuali modifiche a tali informazioni personali.
                </p>
                <p><strong>Informazioni raccolte automaticamente</strong></p>
                <p>
                    <strong>In breve:</strong> alcune informazioni, come
                    l'indirizzo IP (Internet Protocol) e/o le caratteristiche
                    del browser e del dispositivo, vengono raccolte
                    automaticamente quando visiti i nostri Servizi.
                </p>
                <p>
                    Raccogliamo automaticamente determinate informazioni quando
                    visiti, utilizzi o navighi nei Servizi. Queste informazioni
                    non rivelano la tua identità specifica (come il tuo nome o
                    le informazioni di contatto) ma possono includere
                    informazioni sul dispositivo e sull'utilizzo, come il tuo
                    indirizzo IP, le caratteristiche del browser e del
                    dispositivo, il sistema operativo, le preferenze della
                    lingua, gli URL di riferimento, il nome del dispositivo, il
                    paese, la posizione, le informazioni su come e quando
                    utilizzi i nostri Servizi e altre informazioni tecniche.
                    Queste informazioni sono necessarie principalmente per
                    mantenere la sicurezza e il funzionamento dei nostri Servizi
                    e per i nostri scopi interni di analisi e reporting.
                </p>
                <p>
                    Come molte aziende, raccogliamo informazioni anche
                    attraverso cookie e tecnologie simili. Puoi trovare maggiori
                    informazioni al riguardo nella nostra Informativa sui
                    cookie:
                    <a href="https://www.tempiodelfuturo.art/cookies"
                        >www.tempiodelfuturo.art/cookies</a
                    >.
                </p>
                <p>Le informazioni che raccogliamo includono:</p>
                <ul>
                    <li>
                        Dati di registro e di utilizzo. I dati di registro e di
                        utilizzo sono informazioni relative al servizio, alla
                        diagnostica, all'utilizzo e alle prestazioni che i
                        nostri server raccolgono automaticamente quando accedi o
                        utilizzi i nostri Servizi e che registriamo nei file di
                        registro. A seconda di come interagisci con noi, questi
                        dati di registro possono includere il tuo indirizzo IP,
                        le informazioni sul dispositivo, il tipo di browser e le
                        impostazioni e le informazioni sulla tua attività nei
                        Servizi (come i timbri di data / ora associati al tuo
                        utilizzo, pagine e file visualizzati, ricerche e altre
                        azioni che intraprendi come le funzionalità che
                        utilizzi), informazioni sugli eventi del dispositivo
                        (come l'attività del sistema, rapporti sugli errori (a
                        volte chiamati "dump di arresto anomalo") e impostazioni
                        hardware).
                    </li>
                </ul>
                <ul>
                    <li>
                        Dati del dispositivo. Raccogliamo dati sul dispositivo
                        come informazioni sul tuo computer, telefono, tablet o
                        altro dispositivo che utilizzi per accedere ai Servizi.
                        A seconda del dispositivo utilizzato, questi dati del
                        dispositivo possono includere informazioni quali
                        l'indirizzo IP (o il server proxy), i numeri di
                        identificazione del dispositivo e dell'applicazione, la
                        posizione, il tipo di browser, il modello hardware, il
                        provider di servizi Internet e/o l'operatore di
                        telefonia mobile, il sistema operativo e le informazioni
                        sulla configurazione del sistema.
                    </li>
                </ul>
                <ul>
                    <li>
                        Dati sulla posizione. Raccogliamo dati sulla posizione
                        come informazioni sulla posizione del tuo dispositivo,
                        che possono essere precise o imprecise. La quantità di
                        informazioni che raccogliamo dipende dal tipo e dalle
                        impostazioni del dispositivo utilizzato per accedere ai
                        Servizi. Ad esempio, potremmo utilizzare il GPS e altre
                        tecnologie per raccogliere dati di geolocalizzazione che
                        ci indicano la tua posizione attuale (in base al tuo
                        indirizzo IP). Puoi scegliere di non consentirci di
                        raccogliere queste informazioni rifiutando l'accesso
                        alle informazioni o disabilitando l'impostazione della
                        tua posizione sul tuo dispositivo. Tuttavia, se scegli
                        di rinunciare, potresti non essere in grado di
                        utilizzare alcuni aspetti dei Servizi.
                    </li>
                </ul>
                <p id="infouse">
                    <strong>2. COME TRATTIAMO LE TUE INFORMAZIONI?</strong>
                </p>
                <p>
                    <strong>In breve:</strong> elaboriamo le tue informazioni
                    per fornire, migliorare e amministrare i nostri Servizi,
                    comunicare con te, per la sicurezza e la prevenzione delle
                    frodi e per rispettare la legge. Potremmo anche elaborare le
                    tue informazioni per altri scopi con il tuo consenso.
                </p>
                <p>
                    <strong
                        >Elaboriamo le tue informazioni personali per una serie
                        di motivi, a seconda di come interagisci con i nostri
                        Servizi, tra cui:</strong
                    >
                </p>
                <ul>
                    <li>
                        <strong
                            >Per facilitare la creazione e l'autenticazione
                            degli account e gestire in altro modo gli account
                            utente.</strong
                        >
                        Potremmo elaborare le tue informazioni in modo che tu
                        possa creare e accedere al tuo account, nonché mantenere
                        il tuo account funzionante.
                    </li>
                </ul>
                <ul>
                    <li>
                        <strong
                            >Per fornire e facilitare la fornitura di servizi
                            all'utente.</strong
                        >
                        Potremmo elaborare le tue informazioni per fornirti il
                        servizio richiesto.
                    </li>
                </ul>
                <ul>
                    <li>
                        <strong
                            >Per rispondere alle richieste degli utenti/offrire
                            supporto agli utenti.</strong
                        >
                        Potremmo elaborare le tue informazioni per rispondere
                        alle tue richieste e risolvere eventuali problemi che
                        potresti avere con il servizio richiesto.
                    </li>
                </ul>
                <ul>
                    <li>
                        <strong
                            >Per inviarti informazioni amministrative.</strong
                        >
                        Potremmo elaborare le tue informazioni per inviarti
                        dettagli sui nostri prodotti e servizi, modifiche ai
                        nostri termini e politiche e altre informazioni simili.
                    </li>
                </ul>
                <ul>
                    <li>
                        <strong>Per evadere e gestire i tuoi ordini.</strong>
                        Possiamo elaborare le tue informazioni per evadere e
                        gestire i tuoi ordini, pagamenti, resi e cambi
                        effettuati tramite i Servizi.
                    </li>
                </ul>
                <ul>
                    <li>
                        <strong>Per richiedere feedback.</strong> Potremmo
                        elaborare le tue informazioni quando necessario per
                        richiedere feedback e per contattarti in merito al tuo
                        utilizzo dei nostri Servizi.
                    </li>
                </ul>
                <ul>
                    <li>
                        <strong
                            >Per inviarti comunicazioni di marketing e
                            promozionali.</strong
                        >
                        Possiamo elaborare le informazioni personali che ci
                        invii per i nostri scopi di marketing, se ciò è conforme
                        alle tue preferenze di marketing. Puoi disattivare le
                        nostre e-mail di marketing in qualsiasi momento. Per
                        ulteriori informazioni, vedere "<a href="#privacyrights"
                            >QUALI SONO I TUOI DIRITTI ALLA PRIVACY?</a
                        >" sotto.
                    </li>
                </ul>
                <ul>
                    <li>
                        <strong>Per proteggere i nostri Servizi.</strong>
                        Potremmo elaborare le tue informazioni come parte dei
                        nostri sforzi per mantenere i nostri Servizi sicuri e
                        protetti, incluso il monitoraggio e la prevenzione delle
                        frodi.
                    </li>
                </ul>
                <ul>
                    <li>
                        <strong
                            >Per identificare le tendenze di utilizzo.</strong
                        >
                        Potremmo elaborare le informazioni su come utilizzi i
                        nostri Servizi per capire meglio come vengono utilizzati
                        in modo da poterli migliorare.
                    </li>
                </ul>
                <ul>
                    <li>
                        <strong
                            >Per salvare o proteggere l'interesse vitale di un
                            individuo.</strong
                        >
                        Potremmo elaborare le tue informazioni quando necessario
                        per salvare o proteggere l'interesse vitale di un
                        individuo, ad esempio per prevenire danni.
                    </li>
                </ul>
                <p id="legalbases">
                    <strong
                        >3. SU QUALI BASI GIURIDICHE CI BASIAMO PER ELABORARE LE
                        TUE INFORMAZIONI?</strong
                    >
                </p>
                <p>
                    <strong>In breve:</strong> Elaboriamo le tue informazioni
                    personali solo quando riteniamo che sia necessario e abbiamo
                    un valido motivo legale (ad esempio, base legale) per farlo
                    ai sensi della legge applicabile, come con il tuo consenso,
                    per rispettare le leggi, per fornirti servizi per stipulare
                    o adempiere ai nostri obblighi contrattuali, per proteggere
                    i tuoi diritti o per soddisfare i nostri legittimi interessi
                    commerciali.
                </p>
                <p>
                    <strong
                        >Se risiedi nell'UE o nel Regno Unito, questa sezione si
                        applica a te.</strong
                    >
                </p>
                <p>
                    Il Regolamento generale sulla protezione dei dati (GDPR) e
                    il GDPR del Regno Unito ci impongono di spiegare le basi
                    giuridiche valide su cui facciamo affidamento per elaborare
                    le tue informazioni personali. Pertanto, potremmo fare
                    affidamento sulle seguenti basi legali per elaborare le tue
                    informazioni personali:
                </p>
                <ul>
                    <li>
                        <strong>Consenso.</strong> Potremmo elaborare le tue
                        informazioni se ci hai dato il permesso (cioè il
                        consenso) di utilizzare le tue informazioni personali
                        per uno scopo specifico. Puoi revocare il tuo consenso
                        in qualsiasi momento. Ulteriori informazioni sulla
                        <a href="#withdrawconsent">revoca del consenso</a>.
                    </li>
                </ul>
                <ul>
                    <li>
                        <strong>Esecuzione di un contratto.</strong> Potremmo
                        elaborare le tue informazioni personali quando riteniamo
                        che sia necessario adempiere ai nostri obblighi
                        contrattuali nei tuoi confronti, inclusa la fornitura
                        dei nostri Servizi o su tua richiesta prima di stipulare
                        un contratto con te.
                    </li>
                </ul>
                <ul>
                    <li>
                        <strong>Interessi legittimi.</strong> Possiamo elaborare
                        le tue informazioni quando riteniamo che sia
                        ragionevolmente necessario per raggiungere i nostri
                        legittimi interessi commerciali e tali interessi non
                        prevalgono sui tuoi interessi e diritti e libertà
                        fondamentali. Ad esempio, potremmo trattare le tue
                        informazioni personali per alcune delle finalità
                        descritte al fine di:
                    </li>
                </ul>
                <ul>
                    <li>
                        Inviare agli utenti informazioni su offerte speciali e
                        sconti sui nostri prodotti e servizi
                    </li>
                </ul>
                <ul>
                    <li>
                        Analizzare come vengono utilizzati i nostri Servizi in
                        modo da poterli migliorare per coinvolgere e fidelizzare
                        gli utenti
                    </li>
                </ul>
                <ul>
                    <li>
                        Diagnosticare problemi e/o prevenire attività
                        fraudolente
                    </li>
                </ul>
                <ul>
                    <li>
                        Comprendere come i nostri utenti utilizzano i nostri
                        prodotti e servizi in modo da poter migliorare
                        l'esperienza dell'utente
                    </li>
                </ul>
                <ul>
                    <li>
                        <strong>Obblighi di legge.</strong> Potremmo elaborare
                        le tue informazioni laddove riteniamo che sia necessario
                        per il rispetto dei nostri obblighi legali, ad esempio
                        per cooperare con un organismo di contrasto o un'agenzia
                        di regolamentazione, esercitare o difendere i nostri
                        diritti legali o divulgare le tue informazioni come
                        prova in contenziosi in cui siamo coinvolti.
                    </li>
                </ul>
                <ul>
                    <li>
                        <strong>Interessi vitali.</strong> Potremmo elaborare le
                        tue informazioni laddove riteniamo necessario proteggere
                        i tuoi interessi vitali o gli interessi vitali di terzi,
                        come situazioni che comportano potenziali minacce alla
                        sicurezza di qualsiasi persona.
                    </li>
                </ul>
                <p>
                    <strong
                        >Se ti trovi in Canada, questa sezione si applica a
                        te.</strong
                    >
                </p>
                <p>
                    Potremmo elaborare le tue informazioni se ci hai dato
                    un'autorizzazione specifica (cioè il consenso esplicito) per
                    utilizzare le tue informazioni personali per uno scopo
                    specifico o in situazioni in cui la tua autorizzazione può
                    essere dedotta (ad esempio, consenso implicito). Puoi
                    <a href="#withdrawconsent">revocare il tuo consenso</a> in
                    qualsiasi momento.
                </p>
                <p>
                    In alcuni casi eccezionali, potremmo essere legalmente
                    autorizzati dalla legge applicabile a elaborare le tue
                    informazioni senza il tuo consenso, tra cui, ad esempio:
                </p>
                <ul>
                    <li>
                        Se la raccolta è chiaramente nell'interesse di un
                        individuo e il consenso non può essere ottenuto in modo
                        tempestivo
                    </li>
                </ul>
                <ul>
                    <li>
                        Per indagini e rilevamento e prevenzione delle frodi
                    </li>
                </ul>
                <ul>
                    <li>
                        Per le transazioni commerciali, purché siano soddisfatte
                        determinate condizioni
                    </li>
                </ul>
                <ul>
                    <li>
                        Se è contenuto in una testimonianza e la riscossione è
                        necessaria per valutare, elaborare o liquidare un
                        sinistro assicurativo
                    </li>
                </ul>
                <ul>
                    <li>
                        Per identificare persone ferite, malate o decedute e
                        comunicare con i parenti più stretti
                    </li>
                </ul>
                <ul>
                    <li>
                        Se abbiamo ragionevoli motivi per ritenere che un
                        individuo sia stato, sia o possa essere vittima di abusi
                        finanziari
                    </li>
                </ul>
                <ul>
                    <li>
                        Se è ragionevole aspettarsi che la raccolta e l'utilizzo
                        con il consenso compromettano la disponibilità o
                        l'accuratezza delle informazioni e la raccolta sia
                        ragionevole per scopi relativi all'indagine su una
                        violazione di un accordo o una violazione delle leggi
                        del Canada o di una provincia
                    </li>
                </ul>
                <ul>
                    <li>
                        Se la divulgazione è necessaria per ottemperare a un
                        mandato di comparizione, mandato, ordine del tribunale o
                        regole del tribunale relative alla produzione di
                        documenti
                    </li>
                </ul>
                <ul>
                    <li>
                        Se è stato prodotto da un individuo nel corso della sua
                        occupazione, attività o professione e la raccolta è
                        coerente con gli scopi per i quali le informazioni sono
                        state prodotte
                    </li>
                </ul>
                <ul>
                    <li>
                        Se la collezione è esclusivamente per scopi
                        giornalistici, artistici o letterari
                    </li>
                </ul>
                <ul>
                    <li>
                        Se le informazioni sono disponibili al pubblico e sono
                        specificate dai regolamenti
                    </li>
                </ul>
                <p id="whoshare">
                    <strong
                        >4. QUANDO E CON CHI CONDIVIDIAMO LE TUE INFORMAZIONI
                        PERSONALI?</strong
                    >
                </p>
                <p>
                    <strong>In breve:</strong> possiamo condividere informazioni
                    in situazioni specifiche descritte in questa sezione e/o con
                    le seguenti categorie di terze parti.
                </p>
                <p>
                    <strong
                        >Fornitori, consulenti e altri fornitori di servizi di
                        terze parti.</strong
                    >
                    Potremmo condividere i tuoi dati con fornitori di terze
                    parti, fornitori di servizi, appaltatori o agenti ("<strong
                        >terze parti</strong
                    >") che svolgono servizi per noi o per nostro conto e
                    richiedono l'accesso a tali informazioni per svolgere tale
                    lavoro. Abbiamo stipulato contratti con le nostre terze
                    parti, progettati per aiutare a salvaguardare le tue
                    informazioni personali. Ciò significa che non possono fare
                    nulla con le tue informazioni personali a meno che non li
                    abbiamo istruiti a farlo. Inoltre, non condivideranno le tue
                    informazioni personali con alcuna organizzazione diversa da
                    noi. Si impegnano inoltre a proteggere i dati che detengono
                    per nostro conto e a conservarli per il periodo da noi
                    indicato. Le categorie di terze parti con cui possiamo
                    condividere le informazioni personali sono le seguenti:
                </p>
                <ul>
                    <li>Servizi di analisi dei dati</li>
                </ul>
                <ul>
                    <li>Processori di pagamento</li>
                </ul>
                <ul>
                    <li>Strumenti di monitoraggio delle prestazioni</li>
                </ul>
                <ul>
                    <li>Strumenti di vendita e marketing</li>
                </ul>
                <ul>
                    <li>Fornitori di servizi di archiviazione dati</li>
                </ul>
                <p>
                    Potremmo anche aver bisogno di condividere le tue
                    informazioni personali nelle seguenti situazioni:
                </p>
                <ul>
                    <li>
                        <strong>Trasferimenti aziendali.</strong> Potremmo
                        condividere o trasferire le tue informazioni in
                        relazione a, o durante le negoziazioni di, qualsiasi
                        fusione, vendita di beni aziendali, finanziamento o
                        acquisizione di tutta o parte della nostra attività a
                        un'altra società.
                    </li>
                </ul>
                <p id="cookies">
                    <strong
                        >5. UTILIZZIAMO COOKIE E ALTRE TECNOLOGIE DI
                        TRACCIAMENTO?</strong
                    >
                </p>
                <p>
                    <strong>In breve:</strong> potremmo utilizzare cookie e
                    altre tecnologie di tracciamento per raccogliere e
                    archiviare le tue informazioni.
                </p>
                <p>
                    Possiamo utilizzare cookie e tecnologie di tracciamento
                    simili (come web beacon e pixel) per accedere o archiviare
                    informazioni. Informazioni specifiche su come utilizziamo
                    tali tecnologie e su come è possibile rifiutare determinati
                    cookie sono riportate nella nostra Informativa sui cookie:
                    <a href="https://www.tempiodelfuturo.art/cookies"
                        >www.tempiodelfuturo.art/cookies</a
                    >.
                </p>
                <p id="inforetain">
                    <strong
                        >6. PER QUANTO TEMPO CONSERVIAMO LE TUE
                        INFORMAZIONI?</strong
                    >
                </p>
                <p>
                    <strong>In breve:</strong> conserviamo le tue informazioni
                    per tutto il tempo necessario a soddisfare le finalità
                    delineate nella presente informativa sulla privacy, se non
                    diversamente richiesto dalla legge.
                </p>
                <p>
                    Conserveremo le tue informazioni personali solo per il tempo
                    necessario per gli scopi indicati nella presente informativa
                    sulla privacy, a meno che non sia richiesto o consentito
                    dalla legge un periodo di conservazione più lungo (come
                    requisiti fiscali, contabili o altri requisiti legali).
                    Nessuno scopo in questa informativa ci richiederà di
                    conservare le tue informazioni personali per più di dodici
                    (12) mesi dopo la chiusura dell'account dell'utente.
                </p>
                <p>
                    Quando non abbiamo alcuna necessità aziendale legittima in
                    corso per elaborare le tue informazioni personali,
                    elimineremo o renderemo anonime tali informazioni o, se ciò
                    non è possibile (ad esempio, perché le tue informazioni
                    personali sono state archiviate in archivi di backup),
                    memorizzeremo in modo sicuro le tue informazioni personali e
                    le isoleremo da qualsiasi ulteriore elaborazione fino a
                    quando non sarà possibile la cancellazione.
                </p>
                <p id="infosafe">
                    <strong
                        >7. COME MANTENIAMO LE TUE INFORMAZIONI AL
                        SICURO?</strong
                    >
                </p>
                <p>
                    <strong>In breve:</strong> miriamo a proteggere le tue
                    informazioni personali attraverso un sistema di misure di
                    sicurezza organizzative e tecniche.
                </p>
                <p>
                    Abbiamo implementato misure di sicurezza tecniche e
                    organizzative appropriate e ragionevoli progettate per
                    proteggere la sicurezza di tutte le informazioni personali
                    che elaboriamo. Tuttavia, nonostante le nostre garanzie e
                    gli sforzi per proteggere le tue informazioni, nessuna
                    trasmissione elettronica su Internet o tecnologia di
                    archiviazione delle informazioni può essere garantita sicura
                    al 100%, quindi non possiamo promettere o garantire che
                    hacker, criminali informatici o altre terze parti non
                    autorizzate non saranno in grado di sconfiggere la nostra
                    sicurezza e raccogliere, accedere, rubare o modificare
                    impropriamente le tue informazioni. Sebbene faremo del
                    nostro meglio per proteggere le tue informazioni personali,
                    la trasmissione di informazioni personali da e verso i
                    nostri Servizi è a tuo rischio. L'utente deve accedere ai
                    Servizi solo all'interno di un ambiente sicuro.
                </p>
                <p id="privacyrights">
                    <strong>8. QUALI SONO I TUOI DIRITTI SULLA PRIVACY?</strong>
                </p>
                <p>
                    <strong>In breve:</strong> in alcune regioni, come lo Spazio
                    economico europeo (SEE), il Regno Unito (Regno Unito), la
                    Svizzera e il Canada, hai diritti che ti consentono un
                    maggiore accesso e controllo sulle tue informazioni
                    personali. Puoi rivedere, modificare o chiudere il tuo
                    account in qualsiasi momento.
                </p>
                <p>
                    In alcune regioni (come il SEE, il Regno Unito, la Svizzera
                    e il Canada), l'utente gode di determinati diritti ai sensi
                    delle leggi applicabili in materia di protezione dei dati.
                    Questi possono includere il diritto (i) di richiedere
                    l'accesso e ottenere una copia delle informazioni personali,
                    (ii) di richiedere la rettifica o la cancellazione; (iii)
                    limitare il trattamento delle informazioni personali; (vi)
                    se applicabile, alla portabilità dei dati; e (vii) non
                    essere soggetto a processi decisionali automatizzati. In
                    determinate circostanze, potresti anche avere il diritto di
                    opporti al trattamento dei tuoi dati personali. Puoi
                    effettuare tale richiesta contattandoci utilizzando i
                    dettagli di contatto forniti nella sezione "<a
                        href="#contact"
                        >COME PUOI CONTATTARCI IN MERITO A QUESTA
                        INFORMATIVA?</a
                    >" sotto.
                </p>
                <p>
                    Prenderemo in considerazione e agiremo su qualsiasi
                    richiesta in conformità con le leggi sulla protezione dei
                    dati applicabili.
                </p>
                <p>&nbsp;</p>
                <p>
                    Se ti trovi nel SEE o nel Regno Unito e ritieni che stiamo
                    elaborando illegalmente le tue informazioni personali, hai
                    anche il diritto di presentare un reclamo all'autorità per
                    la protezione dei dati tua
                    <a
                        href="https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm"
                        >Member State data protection authority</a
                    >
                    o
                    <a
                        href="https://ico.org.uk/make-a-complaint/data-protection-complaints/data-protection-complaints/"
                        >del Regno Unito</a
                    >.
                </p>
                <p>
                    Se vi trovate in Svizzera, potete rivolgervi
                    <a href="https://www.edoeb.admin.ch/edoeb/en/home.html"
                        >all'Incaricato federale della protezione dei dati e
                        della trasparenza</a
                    >.
                </p>
                <p>
                    <strong>Revoca del tuo consenso:</strong> se facciamo
                    affidamento sul tuo consenso per elaborare le tue
                    informazioni personali, che può essere un consenso esplicito
                    e / o implicito a seconda della legge applicabile, hai il
                    diritto di revocare il tuo consenso in qualsiasi momento.
                    Puoi revocare il tuo consenso in qualsiasi momento
                    contattandoci utilizzando i dettagli di contatto forniti
                    nella sezione "<a href="#contact"
                        >COME PUOI CONTATTARCI IN MERITO A QUESTA
                        INFORMATIVA?</a
                    >" di seguito o aggiornando le tue preferenze.
                </p>
                <p>
                    Tuttavia, si prega di notare che ciò non influirà sulla
                    liceità del trattamento prima del suo ritiro né, quando la
                    legge applicabile lo consente, influirà sul trattamento
                    delle informazioni personali condotto sulla base di motivi
                    legittimi di trattamento diversi dal consenso.
                </p>
                <p>
                    Rinuncia alle comunicazioni di marketing e promozionali: è
                    possibile annullare l'iscrizione
                    <strong
                        >alle nostre comunicazioni di marketing e
                        promozionali</strong
                    >
                    in qualsiasi momento facendo clic sul link di annullamento
                    dell'iscrizione nelle e-mail che inviamo o contattandoci
                    utilizzando i dettagli forniti nella sezione "<a
                        href="#contact"
                        >COME PUOI CONTATTARCI IN MERITO A QUESTO AVVISO?</a
                    >" sotto. Verrai quindi rimosso dagli elenchi di marketing.
                    Tuttavia, potremmo ancora comunicare con te, ad esempio per
                    inviarti messaggi relativi al servizio necessari per
                    l'amministrazione e l'utilizzo del tuo account, per
                    rispondere alle richieste di servizio o per altri scopi non
                    di marketing.
                </p>
                <p><strong>Informazioni sull'account</strong></p>
                <p>
                    Se desideri in qualsiasi momento rivedere o modificare le
                    informazioni nel tuo account o chiudere il tuo account,
                    puoi:
                </p>
                <ul>
                    <li>
                        Accedi alle impostazioni del tuo account e aggiorna il
                        tuo account utente.
                    </li>
                </ul>
                <p>
                    Su tua richiesta di chiudere il tuo account, disattiveremo o
                    elimineremo il tuo account e le informazioni dai nostri
                    database attivi. Tuttavia, potremmo conservare alcune
                    informazioni nei nostri file per prevenire frodi, risolvere
                    problemi, assistere in eventuali indagini, far rispettare i
                    nostri termini legali e / o rispettare i requisiti legali
                    applicabili.
                </p>
                <p>
                    <strong>Cookie e tecnologie simili:</strong> La maggior
                    parte dei browser Web sono impostati per accettare i cookie
                    per impostazione predefinita. Se preferisci, di solito puoi
                    scegliere di impostare il tuo browser per rimuovere i cookie
                    e rifiutare i cookie. Se si sceglie di rimuovere i cookie o
                    rifiutare i cookie, ciò potrebbe influire su determinate
                    funzionalità o servizi dei nostri Servizi. Puoi anche
                    <a href="http://www.aboutads.info/choices/"
                        >disattivare la pubblicità basata sugli interessi da
                        parte degli inserzionisti</a
                    >
                    sui nostri Servizi. Per ulteriori informazioni, consultare
                    la nostra Informativa sui cookie:
                    <a href="https://www.tempiodelfuturo.art/cookies"
                        >www.tempiodelfuturo.art/cookies</a
                    >.
                </p>
                <p>
                    Se hai domande o commenti sui tuoi diritti alla privacy,
                    puoi inviarci un'e-mail all'indirizzo
                    amm.nuovorinascimentoitaliano@gmail.com.
                </p>
                <p id="DNT">
                    <strong>9. CONTROLLI PER LE FUNZIONI DO-NOT-TRACK</strong>
                </p>
                <p>
                    La maggior parte dei browser Web e alcuni sistemi operativi
                    mobili e applicazioni mobili includono una funzione o
                    un'impostazione Do-Not-Track ("DNT") che puoi attivare per
                    segnalare la tua preferenza sulla privacy per non monitorare
                    e raccogliere i dati sulle tue attività di navigazione
                    online. In questa fase non è stato finalizzato uno standard
                    tecnologico uniforme per il riconoscimento e
                    l'implementazione dei segnali DNT. Pertanto, al momento non
                    rispondiamo ai segnali del browser DNT o a qualsiasi altro
                    meccanismo che comunichi automaticamente la tua scelta di
                    non essere tracciato online. Se viene adottato uno standard
                    per il tracciamento online che dobbiamo seguire in futuro,
                    ti informeremo di tale pratica in una versione rivista della
                    presente informativa sulla privacy.
                </p>
                <p id="caresidents">
                    <strong
                        >10. I RESIDENTI IN CALIFORNIA HANNO SPECIFICI DIRITTI
                        ALLA PRIVACY?</strong
                    >
                </p>
                <p>
                    <strong>In breve:</strong> Sì, se risiedi in California, ti
                    vengono concessi diritti specifici relativi all'accesso alle
                    tue informazioni personali.
                </p>
                <p>
                    La Sezione 1798.83 del Codice Civile della California, nota
                    anche come legge "Shine The Light", consente ai nostri
                    utenti residenti in California di richiedere e ottenere da
                    noi, una volta all'anno e gratuitamente, informazioni sulle
                    categorie di informazioni personali (se presenti) che
                    abbiamo divulgato a terzi per scopi di marketing diretto e i
                    nomi e gli indirizzi di tutte le terze parti con cui abbiamo
                    condiviso informazioni personali nell'anno solare
                    immediatamente precedente. Se risiedi in California e
                    desideri effettuare tale richiesta, ti preghiamo di inviarci
                    la tua richiesta per iscritto utilizzando le informazioni di
                    contatto fornite di seguito.
                </p>
                <p>
                    Se hai meno di 18 anni, risiedi in California e hai un
                    account registrato con i Servizi, hai il diritto di
                    richiedere la rimozione dei dati indesiderati che pubblichi
                    pubblicamente sui Servizi. Per richiedere la rimozione di
                    tali dati, ti preghiamo di contattarci utilizzando le
                    informazioni di contatto fornite di seguito e includere
                    l'indirizzo e-mail associato al tuo account e una
                    dichiarazione che risiedi in California. Ci assicureremo che
                    i dati non vengano visualizzati pubblicamente sui Servizi,
                    ma tieni presente che i dati potrebbero non essere rimossi
                    completamente o completamente da tutti i nostri sistemi (ad
                    esempio, backup, ecc.).
                </p>
                <p><strong>Informativa sulla privacy del CCPA</strong></p>
                <p>
                    Il California Code of Regulations definisce un "residente"
                    come:
                </p>
                <p>
                    (1) ogni individuo che si trova nello Stato della California
                    per scopi diversi da quelli temporanei o transitori e
                </p>
                <p>
                    (2) ogni individuo domiciliato nello Stato della California
                    che si trova al di fuori dello Stato della California per
                    uno scopo temporaneo o transitorio
                </p>
                <p>
                    Tutti gli altri individui sono definiti come "non
                    residenti".
                </p>
                <p>
                    Se questa definizione di "residente" si applica a te,
                    dobbiamo rispettare determinati diritti e obblighi relativi
                    alle tue informazioni personali.
                </p>
                <p>
                    <strong
                        >Quali categorie di informazioni personali
                        raccogliamo?</strong
                    >
                </p>
                <p>
                    Abbiamo raccolto le seguenti categorie di informazioni
                    personali negli ultimi dodici (12) mesi:
                </p>
                <table>
                    <tbody>
                        <tr>
                            <td><strong>Categoria</strong></td>
                            <td><strong>Esempi</strong></td>
                            <td><strong>Raccolto</strong></td>
                        </tr>
                        <tr>
                            <td>
                                <p>A. Identificatori</p>
                            </td>
                            <td>
                                <p>
                                    Dettagli di contatto, come nome reale,
                                    alias, indirizzo postale, numero di telefono
                                    o di cellulare, identificatore personale
                                    univoco, identificatore online, indirizzo
                                    IP, indirizzo e-mail e nome dell'account
                                </p>
                            </td>
                            <td>
                                <p>SÌ</p>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p>
                                    B. Categorie di informazioni personali
                                    elencate nello statuto dei registri dei
                                    clienti della California
                                </p>
                            </td>
                            <td>
                                <p>
                                    Nome, informazioni di contatto, istruzione,
                                    occupazione, storia lavorativa e
                                    informazioni finanziarie
                                </p>
                            </td>
                            <td>
                                <p>SÌ</p>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p>
                                    C. Caratteristiche di classificazione
                                    protette ai sensi della legge della
                                    California o federale
                                </p>
                            </td>
                            <td>
                                <p>Data di nascita</p>
                            </td>
                            <td>
                                <p>SÌ</p>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p>D. Informazioni commerciali</p>
                            </td>
                            <td>
                                <p>
                                    Informazioni sulle transazioni, cronologia
                                    degli acquisti, dettagli finanziari e
                                    informazioni di pagamento
                                </p>
                            </td>
                            <td>
                                <p>SÌ</p>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p>E. Informazioni biometriche</p>
                            </td>
                            <td>
                                <p>Impronte digitali e impronte vocali</p>
                            </td>
                            <td>
                                <p>NO</p>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p>
                                    F. Internet o altre attività di rete simili
                                </p>
                            </td>
                            <td>
                                <p>
                                    Cronologia di navigazione, cronologia delle
                                    ricerche, comportamento online, dati sugli
                                    interessi e interazioni con i nostri e altri
                                    siti Web, applicazioni, sistemi e pubblicità
                                </p>
                            </td>
                            <td>
                                <p>NO</p>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p>G. Dati di geolocalizzazione</p>
                            </td>
                            <td>
                                <p>Posizione del dispositivo</p>
                            </td>
                            <td>
                                <p>NO</p>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p>
                                    H. Informazioni audio, elettroniche, visive,
                                    termiche, olfattive o simili
                                </p>
                            </td>
                            <td>
                                <p>
                                    Immagini e registrazioni audio, video o
                                    chiamate create in relazione alle nostre
                                    attività commerciali
                                </p>
                            </td>
                            <td>
                                <p>NO</p>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p>
                                    I. Informazioni professionali o relative
                                    all'occupazione
                                </p>
                            </td>
                            <td>
                                <p>
                                    Dati di contatto aziendali al fine di
                                    fornirti i nostri Servizi a livello
                                    aziendale o titolo di lavoro, storia
                                    lavorativa e qualifiche professionali se ti
                                    candidi per un lavoro con noi
                                </p>
                            </td>
                            <td>
                                <p>NO</p>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p>J. Informazioni sull'istruzione</p>
                            </td>
                            <td>
                                <p>
                                    Record degli studenti e informazioni sulla
                                    directory
                                </p>
                            </td>
                            <td>
                                <p>NO</p>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <p>
                                    K. Inferenze tratte da altre informazioni
                                    personali
                                </p>
                            </td>
                            <td>
                                <p>
                                    Inferenze tratte da una qualsiasi delle
                                    informazioni personali raccolte sopra
                                    elencate per creare un profilo o un
                                    riepilogo su, ad esempio, le preferenze e le
                                    caratteristiche di un individuo
                                </p>
                            </td>
                            <td>
                                <p>NO</p>
                            </td>
                        </tr>
                        <tr>
                            <td>L. Informazioni personali sensibili</td>
                            <td></td>
                            <td>
                                <p>NO</p>
                                <p>&nbsp;</p>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <p>
                    Utilizzeremo e conserveremo le informazioni personali
                    raccolte come necessario per fornire i Servizi o per:
                </p>
                <ul>
                    <li>Categoria A - Finché l'utente ha un account con noi</li>
                </ul>
                <ul>
                    <li>Categoria B - Finché l'utente ha un account con noi</li>
                </ul>
                <ul>
                    <li>Categoria C - Finché l'utente ha un account con noi</li>
                </ul>
                <ul>
                    <li>Categoria D - Finché l'utente ha un account con noi</li>
                </ul>
                <p>
                    Potremmo anche raccogliere altre informazioni personali al
                    di fuori di queste categorie attraverso i casi in cui
                    interagisci con noi di persona, online, per telefono o per
                    posta nel contesto di:
                </p>
                <ul>
                    <li>
                        Ricevere aiuto attraverso i nostri canali di assistenza
                        clienti;
                    </li>
                </ul>
                <ul>
                    <li>
                        Partecipazione a sondaggi o concorsi per i clienti; e
                    </li>
                </ul>
                <ul>
                    <li>
                        Facilitazione nella fornitura dei nostri Servizi e per
                        rispondere alle vostre richieste.
                    </li>
                </ul>
                <p>
                    <strong
                        >Come utilizziamo e condividiamo le tue informazioni
                        personali?</strong
                    >
                </p>
                <p>
                    Raccogliamo e condividiamo le tue informazioni personali
                    attraverso:
                </p>
                <ul>
                    <li>Cookie di targeting/Cookie di marketing</li>
                </ul>
                <ul>
                    <li>Beacon/Pixel/Tag</li>
                </ul>
                <p>
                    Ulteriori informazioni sulle nostre pratiche di raccolta e
                    condivisione dei dati sono disponibili nella presente
                    informativa sulla privacy e nella nostra Informativa sui
                    cookie:
                    <a href="https://www.tempiodelfuturo.art/cookies"
                        >www.tempiodelfuturo.art/cookies</a
                    >.
                </p>
                <p>
                    Puoi scegliere di non vendere o condividere le tue
                    informazioni personali disabilitando i cookie nelle
                    Impostazioni delle preferenze dei cookie e facendo clic sul
                    link Non vendere o condividere le mie informazioni personali
                    sulla nostra home page.
                </p>
                <p>
                    Puoi contattarci via e-mail all'indirizzo
                    amm.nuovorinascimentoitaliano@gmail.com, visitando
                    <a href="https://www.tempiodelfuturo.art/contribute"
                        >www.tempiodelfuturo.art/contribute</a
                    >
                    o facendo riferimento ai dettagli di contatto in fondo a
                    questo documento.
                </p>
                <p>
                    Se stai utilizzando un agente autorizzato per esercitare il
                    tuo diritto di rinuncia, potremmo rifiutare una richiesta se
                    l'agente autorizzato non presenta la prova che sono stati
                    validamente autorizzati ad agire per tuo conto.
                </p>
                <p>
                    <strong
                        >Le tue informazioni saranno condivise con qualcun
                        altro?</strong
                    >
                </p>
                <p>
                    Potremmo divulgare le tue informazioni personali con i
                    nostri fornitori di servizi in base a un contratto scritto
                    tra noi e ciascun fornitore di servizi. Ogni fornitore di
                    servizi è un'entità a scopo di lucro che elabora le
                    informazioni per nostro conto, seguendo gli stessi rigorosi
                    obblighi di protezione della privacy imposti dal CCPA.
                </p>
                <p>
                    Potremmo utilizzare le tue informazioni personali per i
                    nostri scopi commerciali, ad esempio per intraprendere
                    ricerche interne per lo sviluppo tecnologico e la
                    dimostrazione. Questo non è considerato come "vendita" delle
                    tue informazioni personali.
                </p>
                <p>
                    Abbiamo divulgato le seguenti categorie di informazioni
                    personali a terzi per scopi aziendali o commerciali nei
                    dodici (12) mesi precedenti:
                </p>
                <p>
                    Le categorie di terze parti a cui abbiamo divulgato
                    informazioni personali per scopi aziendali o commerciali
                    sono disponibili nella sezione "<a href="#whoshare"
                        >QUANDO E CON CHI CONDIVIDIAMO LE TUE INFORMAZIONI
                        PERSONALI?</a
                    >".
                </p>
                <p>
                    Abbiamo venduto o condiviso le seguenti categorie di
                    informazioni personali a terzi nei dodici (12) mesi
                    precedenti:
                </p>
                <p>
                    Le categorie di terze parti a cui abbiamo venduto
                    informazioni personali sono:
                </p>
                <p>
                    <strong
                        >I tuoi diritti in relazione ai tuoi dati
                        personali</strong
                    >
                </p>
                <p>
                    Diritto di richiedere la cancellazione dei dati — Richiesta
                    di cancellazione
                </p>
                <p>
                    Puoi chiedere la cancellazione delle tue informazioni
                    personali. Se ci chiedi di cancellare le tue informazioni
                    personali, rispetteremo la tua richiesta e cancelleremo le
                    tue informazioni personali, fatte salve alcune eccezioni
                    previste dalla legge, come (ma non limitato a) l'esercizio
                    da parte di un altro consumatore del suo diritto alla
                    libertà di parola, i nostri requisiti di conformità
                    derivanti da un obbligo legale o qualsiasi trattamento che
                    potrebbe essere richiesto per proteggere da attività
                    illegali.
                </p>
                <p>Diritto di essere informato — Richiesta di sapere</p>
                <p>A seconda delle circostanze, hai il diritto di sapere:</p>
                <ul>
                    <li>
                        se raccogliamo e utilizziamo le tue informazioni
                        personali;
                    </li>
                </ul>
                <ul>
                    <li>
                        le categorie di informazioni personali che raccogliamo;
                    </li>
                </ul>
                <ul>
                    <li>
                        gli scopi per i quali vengono utilizzate le informazioni
                        personali raccolte;
                    </li>
                </ul>
                <ul>
                    <li>
                        se vendiamo o condividiamo informazioni personali a
                        terzi;
                    </li>
                </ul>
                <ul>
                    <li>
                        le categorie di informazioni personali che abbiamo
                        venduto, condiviso o divulgato per scopi commerciali;
                    </li>
                </ul>
                <ul>
                    <li>
                        le categorie di terze parti a cui le informazioni
                        personali sono state vendute, condivise o divulgate per
                        scopi commerciali;
                    </li>
                </ul>
                <ul>
                    <li>
                        lo scopo aziendale o commerciale per la raccolta, la
                        vendita o la condivisione di informazioni personali; e
                    </li>
                </ul>
                <ul>
                    <li>
                        le informazioni personali specifiche che abbiamo
                        raccolto su di te.
                    </li>
                </ul>
                <p>
                    In conformità con la legge applicabile, non siamo obbligati
                    a fornire o eliminare le informazioni sui consumatori che
                    sono rese anonime in risposta a una richiesta del
                    consumatore o a re-identificare i singoli dati per
                    verificare una richiesta del consumatore.
                </p>
                <p>
                    Diritto alla non discriminazione per l'esercizio dei diritti
                    alla privacy di un consumatore
                </p>
                <p>
                    Non ti discrimineremo se eserciti i tuoi diritti alla
                    privacy.
                </p>
                <p>
                    Diritto di limitare l'uso e la divulgazione di informazioni
                    personali sensibili
                </p>
                <p>
                    Non elaboriamo le informazioni personali sensibili dei
                    consumatori.
                </p>
                <p>Processo di verifica</p>
                <p>
                    Dopo aver ricevuto la tua richiesta, dovremo verificare la
                    tua identità per determinare che sei la stessa persona su
                    cui abbiamo le informazioni nel nostro sistema. Questi
                    sforzi di verifica ci impongono di chiederti di fornire
                    informazioni in modo da poterle abbinare alle informazioni
                    che ci hai fornito in precedenza. Ad esempio, a seconda del
                    tipo di richiesta inviata, potremmo chiederti di fornire
                    determinate informazioni in modo da poter abbinare le
                    informazioni fornite con le informazioni che abbiamo già in
                    archivio, oppure potremmo contattarti tramite un metodo di
                    comunicazione (ad esempio, telefono o e-mail) che ci hai
                    precedentemente fornito. Potremmo anche utilizzare altri
                    metodi di verifica a seconda delle circostanze.
                </p>
                <p>
                    Utilizzeremo le informazioni personali fornite nella tua
                    richiesta solo per verificare la tua identità o l'autorità
                    per effettuare la richiesta. Per quanto possibile, eviteremo
                    di richiederti ulteriori informazioni a scopo di verifica.
                    Tuttavia, se non siamo in grado di verificare la tua
                    identità dalle informazioni già da noi mantenute, potremmo
                    chiederti di fornire ulteriori informazioni allo scopo di
                    verificare la tua identità e per motivi di sicurezza o
                    prevenzione delle frodi. Cancelleremo tali informazioni
                    aggiuntive non appena avremo finito di verificarti.
                </p>
                <p>Altri diritti sulla privacy</p>
                <ul>
                    <li>
                        Puoi opporti al trattamento dei tuoi dati personali.
                    </li>
                </ul>
                <ul>
                    <li>
                        Puoi richiedere la correzione dei tuoi dati personali se
                        non sono corretti o non più pertinenti, o chiedere di
                        limitare il trattamento delle informazioni.
                    </li>
                </ul>
                <ul>
                    <li>
                        Puoi designare un agente autorizzato per presentare una
                        richiesta ai sensi del CCPA per tuo conto. Potremmo
                        rifiutare una richiesta da parte di un agente
                        autorizzato che non presenta la prova di essere stato
                        validamente autorizzato ad agire per tuo conto in
                        conformità con il CCPA.
                    </li>
                </ul>
                <p>
                    Per esercitare tali diritti, è possibile contattarci via
                    e-mail all'indirizzo amm.nuovorinascimentoitaliano@gmail.com, visitando
                    <a href="https://www.tempiodelfuturo.art/u/manage"
                        >la tua pagina di gestione del profilo</a
                    >
                    o facendo riferimento ai dettagli di contatto in fondo a
                    questo documento. Se hai un reclamo su come gestiamo i tuoi
                    dati, vorremmo sentirti.
                </p>
                <p id="virginia">
                    <strong
                        >11. I RESIDENTI IN VIRGINIA HANNO SPECIFICI DIRITTI
                        SULLA PRIVACY?</strong
                    >
                </p>
                <p>
                    <strong>In breve:</strong> sì, se risiedi in Virginia, ti
                    possono essere concessi diritti specifici relativi
                    all'accesso e all'uso delle tue informazioni personali.
                </p>
                <p>
                    <strong>Informativa sulla privacy di Virginia CDPA</strong>
                </p>
                <p>
                    Ai sensi del Virginia Consumer Data Protection Act (CDPA):
                </p>
                <p>
                    "Consumatore" indica una persona fisica residente nel
                    Commonwealth che agisce solo in un contesto individuale o
                    familiare. Non comprende una persona fisica che agisce in un
                    contesto commerciale o lavorativo.
                </p>
                <p>
                    Per "dati personali" si intende qualsiasi informazione
                    collegata o ragionevolmente collegabile a una persona fisica
                    identificata o identificabile. I "dati personali" non
                    includono dati anonimizzati o informazioni disponibili
                    pubblicamente.
                </p>
                <p>
                    Per "Vendita di dati personali" si intende lo scambio di
                    dati personali dietro corrispettivo.
                </p>
                <p>
                    Se questa definizione di "consumatore" si applica a te,
                    dobbiamo rispettare determinati diritti e obblighi relativi
                    ai tuoi dati personali.
                </p>
                <p>
                    Le informazioni che raccogliamo, utilizziamo e divulghiamo
                    su di te varieranno a seconda di come interagisci con noi e
                    i nostri Servizi. Per saperne di più, visita i seguenti
                    link:
                </p>
                <ul>
                    <li>
                        <a href="#infocollect"
                            >Dati personali che raccogliamo</a
                        >
                    </li>
                </ul>
                <ul>
                    <li>
                        <a href="#infouse"
                            >Come utilizziamo i tuoi dati personali</a
                        >
                    </li>
                </ul>
                <ul>
                    <li>
                        <a href="#whoshare"
                            >Quando e con chi condividiamo i tuoi dati
                            personali</a
                        >
                    </li>
                </ul>
                <p>I tuoi diritti in relazione ai tuoi dati personali</p>
                <ul>
                    <li>
                        Diritto di essere informato se stiamo trattando o meno i
                        tuoi dati personali
                    </li>
                </ul>
                <ul>
                    <li>Diritto di accesso ai propri dati personali</li>
                </ul>
                <ul>
                    <li>
                        Diritto di correggere inesattezze nei tuoi dati
                        personali
                    </li>
                </ul>
                <ul>
                    <li>
                        Diritto di richiedere la cancellazione dei propri dati
                        personali
                    </li>
                </ul>
                <ul>
                    <li>
                        Diritto di ottenere una copia dei dati personali che hai
                        precedentemente condiviso con noi
                    </li>
                </ul>
                <ul>
                    <li>
                        Diritto di opporsi al trattamento dei propri dati
                        personali se vengono utilizzati per pubblicità mirata,
                        vendita di dati personali o profilazione a sostegno di
                        decisioni che producono effetti legali o analogamente
                        significativi ("profilazione")
                    </li>
                </ul>
                <p>
                    Non abbiamo venduto alcun dato personale a terzi per scopi
                    aziendali o commerciali. In futuro non venderemo dati
                    personali appartenenti a visitatori del sito web, utenti e
                    altri consumatori.
                </p>
                <p>Esercitare i diritti previsti dal CDPA della Virginia</p>
                <p>
                    Ulteriori informazioni sulle nostre pratiche di raccolta e
                    condivisione dei dati sono disponibili nella presente
                    informativa sulla privacy e nella nostra Informativa sui
                    cookie:
                    <a href="https://www.tempiodelfuturo.art/cookies"
                        >www.tempiodelfuturo.art/cookies</a
                    >.
                </p>
                <p>
                    Puoi contattarci via e-mail all'indirizzo
                    amm.nuovorinascimentoitaliano@gmail.com, inviando una
                    <a
                        href="https://app.termly.io/notify/ae923c0a-fbc6-4be3-8855-737fa7a65665"
                        >richiesta</a
                    >
                    di accesso ai dati dell'interessato o facendo riferimento ai
                    dettagli di contatto in fondo a questo documento.
                </p>
                <p>
                    Se stai utilizzando un agente autorizzato per esercitare i
                    tuoi diritti, potremmo rifiutare una richiesta se l'agente
                    autorizzato non presenta la prova che sono stati validamente
                    autorizzati ad agire per tuo conto.
                </p>
                <p>Processo di verifica</p>
                <p>
                    Potremmo chiederti di fornire ulteriori informazioni
                    ragionevolmente necessarie per verificare te e la richiesta
                    del tuo consumatore. Se invii la richiesta tramite un agente
                    autorizzato, potremmo aver bisogno di raccogliere ulteriori
                    informazioni per verificare la tua identità prima di
                    elaborare la tua richiesta.
                </p>
                <p>
                    Dopo aver ricevuto la tua richiesta, risponderemo senza
                    indebito ritardo, ma in tutti i casi, entro quarantacinque
                    (45) giorni dal ricevimento. Il periodo di risposta può
                    essere esteso una volta di quarantacinque (45) giorni
                    aggiuntivi quando ragionevolmente necessario. Ti informeremo
                    di tale proroga entro il periodo di risposta iniziale di 45
                    giorni, insieme al motivo della proroga.
                </p>
                <p>Diritto di ricorso</p>
                <p>
                    Se rifiutiamo di agire in merito alla tua richiesta, ti
                    informeremo della nostra decisione e del ragionamento alla
                    base di essa. Se desideri appellarti alla nostra decisione,
                    ti preghiamo di inviarci un'e-mail all'indirizzo
                    amm.nuovorinascimentoitaliano@gmail.com. Entro sessanta (60)
                    giorni dal ricevimento di un ricorso, vi informeremo per
                    iscritto di qualsiasi azione intrapresa o non intrapresa in
                    risposta al ricorso, compresa una spiegazione scritta dei
                    motivi delle decisioni. Se il tuo ricorso viene respinto,
                    puoi contattare il
                    <a
                        href="https://www.oag.state.va.us/consumer-protection/index.php/file-a-complaint"
                        >procuratore generale per presentare una denuncia</a
                    >.
                </p>
                <p id="policyupdates">
                    <strong
                        >12. APPORTIAMO AGGIORNAMENTI ALLA PRESENTE
                        INFORMATIVA?</strong
                    >
                </p>
                <p>
                    <strong>In breve:</strong> Sì, aggiorneremo questa
                    informativa se necessario per rimanere conformi alle leggi
                    pertinenti.
                </p>
                <p>
                    Potremmo aggiornare la presente informativa sulla privacy di
                    volta in volta. La versione aggiornata sarà indicata da una
                    data aggiornata "Rivista" e la versione aggiornata sarà
                    effettiva non appena sarà accessibile. Se apportiamo
                    modifiche sostanziali alla presente informativa sulla
                    privacy, potremmo informarti pubblicando in modo visibile un
                    avviso di tali modifiche o inviandoti direttamente una
                    notifica. Ti invitiamo a rivedere frequentemente questa
                    informativa sulla privacy per essere informato su come
                    stiamo proteggendo le tue informazioni.
                </p>
                <p id="contact">
                    <strong
                        >13. COME PUOI CONTATTARCI IN MERITO A QUESTA
                        INFORMATIVA?</strong
                    >
                </p>
                <p>
                    In caso di domande o commenti sulla presente informativa, è
                    possibile contattare il nostro Responsabile della protezione
                    dei dati (DPO), Nuovo Rinascimento APS, via e-mail
                    all'indirizzo amm.nuovorinascimentoitaliano@gmail.com, o
                    contattarci per posta all'indirizzo:
                </p>
                <p>Nuovo Rinascimento APS</p>
                <p>Via Luigi Nono, 7, 20154 Milano MI, Italia</p>
                <p>Milano, Lombardia 20154</p>
                <p>Italia</p>
                <p id="request">
                    <strong
                        >14. COME PUOI RIVEDERE, AGGIORNARE O ELIMINARE I DATI
                        CHE RACCOGLIAMO DA TE?</strong
                    >
                </p>
                <p>
                    In base alle leggi applicabili del tuo paese, potresti avere
                    il diritto di richiedere l'accesso alle informazioni
                    personali che raccogliamo da te, modificare tali
                    informazioni o eliminarle. Per richiedere di rivedere,
                    aggiornare o eliminare le tue informazioni personali,
                    compila e invia un
                    <a
                        href="https://app.termly.io/notify/ae923c0a-fbc6-4be3-8855-737fa7a65665"
                        >Richiesta</a
                    >
                    di accesso dell'interessato.
                </p>
                <!-- <p>
                    Questa politica è stata creata utilizzando
                    <a
                        href="https://termly.io/it/products/privacy-policy-generator/?ftseo"
                        >Termly's Privacy Policy Generator</a
                    >.
                </p> -->
            </div>
        </div>
    </section>
</template>

<style lang="scss" scoped>
.hero {
    > * {
        color: var(--color-subtle) !important;
    }
}
.hero-body {
    @media screen and (max-width: 768px) {
        max-width: 95vw;
    }
    // padding: 0 1rem;
}
p > strong,
li > strong {
    color: var(--color-primary-alt);
    font-weight: 700;
    font-size: 1.2rem;
}
p {
    scroll-behavior: smooth;
    scroll-snap-type: y mandatory;
    scroll-margin-top: 4rem;
    margin-bottom: 1rem;
}
p > strong {
    display: block;
}
table {
    word-break: break-word;

    border-collapse: collapse;
    border-spacing: 0;
    margin-bottom: 1rem;
    width: 100%;
    border: 1px solid var(--color-primary-alt);
    th,
    td {
        strong {
            color: var(--color-primary-alt);
            font-weight: 700;
            text-transform: uppercase;
        }
        // first child
        &:first-child {
            width: 10rem;
        }
        text-align: left;
        padding: 0.5rem;
        border: 1px solid var(--color-primary-alt);
    }
    th {
        background-color: var(--bg-secondary);
        color: var(--color-subtle);
    }
    tr:nth-child(even) {
        background-color: var(--bg-secondary);
        color: var(--color-subtle);
    }
    tr:nth-child(odd) {
        background-color: var(--bg-secondary);
        color: var(--color-subtle);
    }
}
a {
    color: var(--color-primary) !important;
    // text-decoration: underline;
    text-transform: uppercase;
    font-weight: 700;
    margin: 0 0.25rem;
    display: inline !important;
}

// list styling

ul {
    list-style: none;
    padding-left: 0;
    margin-left: 0;
    // margin-bottom: 1rem;
    li {
        margin-left: 0;
        margin-bottom: 1rem;
        &:before {
            content: "•";
            color: var(--color-primary-alt);
            display: inline-block;
            width: 1rem;
            margin-left: 1rem;
        }
    }
}
</style>
<script>
export default {
    name: "PrivacyPolicy",
    data() {
        return {
            pageTitle: "TDF*P — " + this.$t("privacy.meta.title"),
            height: "100%",
        };
    },
    layout: "default",

    mounted() {
        this.$store.commit("setCurrPageTitle", this.$t("privacy.title"));
        if (this.$i18n.locale === "en") {
            //redirecet to privacy-en
            this.$router.push("/en/privacy-en");
        }
        this.enterAnimation();
        // load termly embed script
    },
    head() {
        return {
            title: this.pageTitle,
        };
    },
    watch: {
        $route(to, from) {
            this.$store.commit("setCurrPageTitle", this.$t("privacy.title"));
        },
    },

    methods: {
        enterAnimation() {
            const anime = this.$anime;
            anime({
                targets: ".main-content",
                easing: "easeInOutQuad",
                opacity: [0, 1],
                filter: ["blur(2rem)", "blur(0px)"],
                duration: 360,
            });
        },
    },
    beforeMount() {
        window.addEventListener("scroll", this.handleScroll);
    },
    beforeDestroy() {
        window.removeEventListener("scroll", this.handleScroll);
    },
};
</script>
